import FullCalendar from '@fullcalendar/react'
import dayGridPlugin from '@fullcalendar/daygrid'
import timeGridPlugin from '@fullcalendar/timegrid'
import ptBrLocale from '@fullcalendar/core/locales/pt-br'
import interactionPlugin from '@fullcalendar/interaction'
import { DateSelectArg, DatesSetArg, EventClickArg, EventContentArg } from '@fullcalendar/core'
import { FC, ReactNode } from 'react'
import styles from './styles/CustomCalendar.module.css'
import { InfoTooltip } from '../Info/InfoTooltip'

type CalendarView = 'dayGridMonth' | 'timeGridWeek' | 'timeGridDay' | 'listWeek' | 'listMonth'

type VisibleRange = {
  inicio: string
  termino: string
}

export type ICustomCalendarData = {
  title?: string
  start: string
  end: string
  color?: string
  display?: 'block' | 'auto'
  id: string
  profissional?: string
  especialidade?: string
  especialidadeColor?: string
  quantidade?: number
  /**
   * Dados livres do evento, repassados ao FullCalendar e lidos em
   * `eventContent` via `arg.event.extendedProps`. Use isto em vez de criar um
   * campo novo aqui para cada feature.
   */
  extendedProps?: Record<string, unknown>
}

type ICustomCalendar = {
  events: ICustomCalendarData[]
  onClick?: (clickInfo: EventClickArg) => void
  onDateClick?: (clickDate: DateSelectArg) => void
  onVisibleRangeChange?: (range: VisibleRange) => void
  allowedViews?: CalendarView[]
  /**
   * Mostra a faixa de eventos de dia inteiro nas visões de semana e dia.
   * Default `false` para preservar o comportamento de quem já usa o
   * componente; ligue quando a tela tiver bloqueios de dia inteiro, que de
   * outro modo simplesmente não renderizam fora da visão de mês.
   */
  allDaySlot?: boolean
  timeZone?: 'UTC' | 'local'
  eventContent?: (arg: EventContentArg) => ReactNode
}
export const CustomCalendar: FC<ICustomCalendar> = ({
  events,
  onClick,
  onDateClick,
  onVisibleRangeChange,
  allowedViews,
  allDaySlot = false,
  timeZone,
  eventContent,
}) => {
  const normalizedEvents = events.map((event) => ({
    ...event,
    display: event.display ?? 'block',
    id: String(event.id),
  }))

  const handleDatesSet = (arg: DatesSetArg) => {
    const inicio = arg.start.toISOString().split('T')[0]
    const endDate = new Date(arg.end)
    endDate.setDate(endDate.getDate() - 1)
    const termino = endDate.toISOString().split('T')[0]
    onVisibleRangeChange?.({ inicio, termino })
  }

  const defaultEventContent = (arg: EventContentArg) => {
    const profissional = arg.event.extendedProps.profissional as string | undefined
    const especialidadeColor = arg.event.extendedProps.especialidadeColor
    const inicio = arg.event.start
    const fim = arg.event.end
    const especialidade = arg.event.extendedProps.especialidade as string | undefined
    const quantidade = arg.event.extendedProps.quantidade as number | undefined | null

    const formatTime = (date?: Date | null) =>
      date
        ? date.toLocaleTimeString('pt-BR', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: false,
          })
        : ''

    return (
      <InfoTooltip
        text={
          <div>
            <div className='d-flex justify-content-between align-items-start'>
              <strong className='fs-6 text-truncate' title={profissional}>
                {profissional}
              </strong>

              <span className='ms-2 flex-shrink-0'>{formatTime(inicio)}</span>
            </div>

            <div className='d-flex justify-content-between align-items-start mt-1'>
              {especialidade && (
                <span className='text-muted text-truncate' title={especialidade}>
                  {especialidade}
                </span>
              )}

              <span className='ms-2 flex-shrink-0'>{formatTime(fim)}</span>
            </div>

            {quantidade !== null && quantidade !== undefined && (
              <div className='mt-3 text-muted'>
                <strong>{quantidade} consultas</strong>
              </div>
            )}
          </div>
        }
      >
        <div
          className={`${styles.event} mx-1 px-2 py-2`}
          style={{ borderLeftColor: especialidadeColor }}
        >
          <div className='d-flex justify-content-between align-items-center'>
            {profissional && (
              <div className={`${styles.profissional}`} title={profissional}>
                {profissional}
              </div>
            )}
            {inicio && <div className={styles.time}>{formatTime(inicio)}</div>}
          </div>

          <div className='d-flex justify-content-between align-items-center'>
            {especialidade && <div className={`${styles.time} fs-8`}>{especialidade}</div>}
            {fim && <div className={styles.time}>{formatTime(fim)}</div>}
          </div>

          {quantidade !== null && quantidade !== undefined && (
            <div className='d-flex flex-end mt-2'>
              <strong className='text-body'>{quantidade} consultas</strong>
            </div>
          )}
        </div>
      </InfoTooltip>
    )
  }

  return (
    <FullCalendar
      timeZone={timeZone}
      plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
      initialView={allowedViews ? allowedViews[0] : 'dayGridMonth'}
      locale={ptBrLocale}
      headerToolbar={{
        start: 'prev,next today',
        center: 'title',
        end: allowedViews && allowedViews.length > 1 ? allowedViews.join(',') : '',
      }}
      eventContent={eventContent ?? defaultEventContent}
      selectable={true}
      eventClick={onClick}
      select={onDateClick}
      allDaySlot={allDaySlot}
      nowIndicator={true}
      events={normalizedEvents}
      /* slotMinTime='7:00:00'
      slotMaxTime='20:00:00'
      editable={true}
      dateClick={onDateClick} */
      displayEventEnd
      titleFormat={(arg) => {
        const date = new Date(
          arg.date.year,
          arg.date.month,
          arg.date.day,
          arg.date.hour,
          arg.date.minute,
          arg.date.second
        )
        const mes = date.toLocaleDateString('pt-BR', { month: 'long' })
        const ano = date.getFullYear()
        return `${mes.toUpperCase()} ${ano}`
      }}
      dayHeaderContent={(arg) => <span>{arg.text.replace(/[^a-zA-ZÀ-ÿ]/g, '').toUpperCase()}</span>}
      eventTimeFormat={{
        hour: '2-digit',
        minute: '2-digit',
        meridiem: false,
        hour12: false,
      }}
      datesSet={handleDatesSet}
      height='auto'
      expandRows
    />
  )
}
